import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { accountUrl } from "@/lib/account";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/primitives";
import { Eyebrow, H2 } from "@/components/home/shared";

const PERKS = [["Move up", "Every signup through your link moves you up the early-access queue."], ["Founder price", "Referrers keep founder pricing locked in when the full terminal launches."], ["20% recurring", "At launch, the program becomes 20% recurring commission on every paid seat you refer."]];
const STEPS = [["01", "Get your link", "Create a free account to get your personal referral link."], ["02", "Share it", "Post it on X, Telegram or Discord, or send it to your trading group."], ["03", "Climb & earn", "Move up the early-access queue now. Earn recurring commission from launch."]];

const GetLink = () => (
  <div data-testid="referral-get-link">
    <a href={accountUrl()} data-testid="referral-create-account-btn"
      className="group inline-flex items-center gap-2 bg-[#BACD1C] px-7 py-4 font-mono text-[13px] uppercase tracking-[0.14em] text-black transition-colors duration-300 hover:bg-[#ECEBE4]">
      Create free account <ArrowUpRight size={16} />
    </a>
    <p className="mt-4 font-mono text-[11px] text-[#7D8390]">Your personal referral link lives in your TradrQuant account.</p>
  </div>
);

export default function Referral() {
  useSeo({ title: "TradrQuant Referral Program | Founder pricing + 20% recurring", description: "Refer traders to TradrQuant: move up the early-access queue, keep founder pricing, and earn 20% recurring commission from launch.", path: "/referral" });
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="referral-page" className="min-h-screen bg-[#08090B] text-[#ECEBE4]">
      <header className="border-b border-white/[0.07]">
        <div className="mx-auto flex h-14 max-w-[1400px] items-center justify-between px-6 md:px-12">
          <Link to="/" className="flex items-center gap-2.5"><img src="/tq-logo.png?v=3" alt="TradrQuant" className="h-[22px] w-auto" /><span data-testid="wordmark" className="tq-wordmark text-[#ECEBE4] text-[21px]">Tradr<span className="tq-q">Q</span>uant</span></Link>
          <Link to="/" data-testid="referral-back-btn" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#7D8390] hover:text-[#ECEBE4]"><ChevronLeft size={14} /> Home</Link>
        </div>
      </header>
      <section className="mx-auto max-w-[1400px] px-6 pb-20 pt-20 md:px-12 md:pt-28">
        <Reveal className="max-w-4xl">
          <Eyebrow>Referral program · pre-launch</Eyebrow>
          <h1 className="mt-6 font-display text-[2.5rem] font-semibold leading-[1] tracking-[-0.04em] sm:text-6xl lg:text-7xl">Give founder pricing. <span className="text-[#7D8390]">Earn every month.</span></h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#9DA3AE]">Bring traders to the institutional terminal built for retail. Climb the queue now and earn 20% recurring commission from launch.</p>
        </Reveal>
        <Reveal delay={0.1} className="mt-10 max-w-2xl"><GetLink /></Reveal>
      </section>
      <section className="border-t border-white/[0.06]">
        <div className="mx-auto grid max-w-[1400px] gap-px bg-white/[0.07] md:grid-cols-3">
          {PERKS.map(([a, b]) => (
            <div key={a} data-testid={`referral-perk-${a.split(" ")[0].toLowerCase()}`} className="bg-[#08090B] px-6 py-12 md:px-12">
              <div className="font-display text-4xl font-semibold tracking-[-0.03em]">{a}</div>
              <p className="mt-3 max-w-xs text-[15px] leading-relaxed text-[#9DA3AE]">{b}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <H2>How it works</H2>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {STEPS.map(([n, t, b]) => (
              <div key={n} className="border-t border-white/[0.12] pt-6"><span className="font-mono text-[11px] tracking-[0.2em] text-[#BACD1C]">{n}</span><h3 className="mt-3 font-display text-2xl font-semibold tracking-tight">{t}</h3><p className="mt-2 text-[15px] text-[#9DA3AE]">{b}</p></div>
            ))}
          </div>
        </div>
      </section>
      <section className="border-t border-white/[0.06] py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.18em] text-[#5A6170]">Commission activates at full launch; full program terms will be published then. Self-referrals don't count. Not financial advice.</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
